import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createPurchaseOrder, listPurchaseOrders } from "../../api/purchaseOrdersApi";
import { listInventory } from "../../api/inventoryApi";
import type { InventoryItemOut, PurchaseOrderCreate, PurchaseOrderLineCreate, PurchaseOrderOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { SupplierSelect } from "../../components/SupplierSelect";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWritePurchaseOrders } from "../../roles/permissions";

const STATUS_BADGE_CLASS: Record<string, string> = {
  DRAFT: "badge--due-soon",
  PENDING_APPROVAL: "badge--due-soon",
  ORDERED: "badge--current",
  PARTIALLY_RECEIVED: "badge--current",
  RECEIVED: "badge--paid",
  CANCELLED: "badge--overdue",
};

interface LineDraft {
  key: number;
  inventoryItemId: string;
  quantity: string;
  unitCost: string;
}

let lineKeySeq = 0;
function newLine(): LineDraft {
  return { key: ++lineKeySeq, inventoryItemId: "", quantity: "", unitCost: "" };
}

const COLUMNS: DataTableColumn<PurchaseOrderOut, keyof PurchaseOrderOut>[] = [
  {
    key: "id",
    header: "PO",
    render: (r) => <Link to={`/purchase-orders/${r.id}`}>{r.id.slice(0, 8)}</Link>,
  },
  {
    key: "status",
    header: "Status",
    render: (r) => (
      <>
        <span className={`badge ${STATUS_BADGE_CLASS[r.status] ?? "badge--current"}`}>{r.status}</span>
        {r.status === "PENDING_APPROVAL" ? (
          <>
            {" "}
            <Link to="/approvals">review</Link>
          </>
        ) : null}
      </>
    ),
  },
  { key: "supplier_id", header: "Supplier", render: (r) => (r.supplier_id ? <span className="muted">{r.supplier_id.slice(0, 8)}</span> : "—") },
  { key: "branch_id", header: "Branch", render: (r) => <span className="muted">{r.branch_id.slice(0, 8)}</span> },
  { key: "lines", header: "Lines", render: (r) => r.lines.length },
];

/**
 * Purchase orders — `POST/GET /purchase-orders` (§10.4). Create a PO against a supplier +
 * branch with line items; receiving (partial supported) happens on the detail page.
 */
export function PurchaseOrdersPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWritePurchaseOrders(user.role) : false;
  const posState = useAsyncData(() => ensureFreshToken().then((token) => listPurchaseOrders(token)), []);
  const { pageData: posPageData, pagination: posPagination, setPage: setPosPage } = usePagination(posState.data ?? [], 25);

  const [branchId, setBranchId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [items, setItems] = useState<InventoryItemOut[]>([]);
  useEffect(() => {
    if (!branchId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    ensureFreshToken()
      .then((token) => listInventory(token, branchId))
      .then((result) => {
        if (!cancelled) setItems(result);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, ensureFreshToken]);

  function updateLine(key: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function removeLine(key: number) {
    setLines((prev) => (prev.length > 1 ? prev.filter((line) => line.key !== key) : prev));
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const validLines = lines.filter((line) => line.inventoryItemId && line.quantity && line.unitCost);
    if (!branchId) {
      setFormError("Select a branch.");
      return;
    }
    if (validLines.length === 0) {
      setFormError("Add at least one line item (item, quantity, and unit cost).");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const body: PurchaseOrderCreate = {
        branch_id: branchId,
        supplier_id: supplierId || undefined,
        notes: notes || undefined,
        lines: validLines.map(
          (line): PurchaseOrderLineCreate => ({
            inventory_item_id: line.inventoryItemId,
            quantity_ordered: asDecimalString(line.quantity),
            unit_cost: asDecimalString(line.unitCost),
          }),
        ),
      };
      await createPurchaseOrder(token, body);
      setLines([newLine()]);
      setNotes("");
      posState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create purchase order.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Purchase Orders</h1>
          <p className="page__subtitle">Order stock from a supplier for a branch; receive it (partial receiving supported) on the detail page.</p>
        </div>
      </div>

      {posState.error ? <StatusBanner kind="error">{posState.error}</StatusBanner> : null}

      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) has read access to purchase orders. Creating/receiving is restricted to
          OWNER/BRANCH_MANAGER/ECOM_MGR by the backend.
        </StatusBanner>
      ) : null}

      {canWrite ? (
      <div className="panel">
        <h2 className="panel__title">New purchase order</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <BranchSelect id="po-branch" label="Branch" value={branchId} onChange={setBranchId} required />
            <SupplierSelect id="po-supplier" label="Supplier" value={supplierId} onChange={setSupplierId} />
            <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <h3 className="panel__title" style={{ fontSize: "0.95rem" }}>
            Line items
          </h3>
          {!branchId ? <p className="muted">Select a branch first to choose items stocked there.</p> : null}
          {lines.map((line) => (
            <div key={line.key} className="leg-row">
              <div className="sl-field">
                <label className="sl-field__label" htmlFor={`po-item-${line.key}`}>
                  Item
                </label>
                <select
                  id={`po-item-${line.key}`}
                  className="sl-input"
                  value={line.inventoryItemId}
                  onChange={(e) => updateLine(line.key, { inventoryItemId: e.target.value })}
                  disabled={!branchId}
                >
                  <option value="">Select item…</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.sku} — {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Quantity"
                inputMode="decimal"
                value={line.quantity}
                onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
              />
              <Input
                label="Unit cost"
                inputMode="decimal"
                value={line.unitCost}
                onChange={(e) => updateLine(line.key, { unitCost: e.target.value })}
              />
              <Button type="button" variant="ghost" onClick={() => removeLine(line.key)} disabled={lines.length === 1}>
                Remove
              </Button>
            </div>
          ))}
          <div className="form-actions" style={{ marginBottom: 16 }}>
            <Button type="button" variant="secondary" onClick={() => setLines((prev) => [...prev, newLine()])}>
              + Add line
            </Button>
          </div>

          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create purchase order"}
            </Button>
          </div>
        </form>
      </div>
      ) : null}

      <div className="panel">
        {posState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={posPageData}
            getRowId={(row) => row.id}
            pagination={posPagination}
            onPageChange={setPosPage}
            emptyMessage="No purchase orders yet."
            caption="Purchase Orders"
          />
        )}
      </div>
    </div>
  );
}
